import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, CircularProgress, Paper, Stack, Tab, Tabs } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useSearchParams } from "react-router-dom";
import { useStores } from "../../../use-stores";
import { useNotification } from "../../../../../utils/NotificationsContext";
import { Swagpack } from "../../../../../models/Swagstore/Packs";
import type { FilterValue } from "../../../../../models/Filters/FilterValue";
import FormContent from "../../../../../views/components/common/form/FormContent";
import FormTextField from "../../../../../views/components/common/form/FormTextField";
import PageTitle from "../../../../../views/components/common/pages/PageTitle";
import CatalogueProductGrid from "./mockups/CatalogueProductGrid";
import MockupGeneratorModal from "./mockups/MockupGeneratorModal";
import StorePrintingFilesDialog from "./mockups/StorePrintingFilesDialog";
import AppLoader from "../../../../../views/components/common/pages/AppLoader";
import AppTablePagenation from "../../../../../views/components/common/pages/AppTablePagenation";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import SwagstoreMockupManagerPage from "./mockups/SwagstoreMockupManagerPage";
import AppDropDown from "../../../../../views/components/common/filter/AppDropDown";
import { GeneratedMockupApprovalStatus } from "../../../../../models/MockupConfig/GeneratedMockup";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import { catalogSkuChoices, catalogSkuForRequest, mockupMatchesCatalogSku, PRODUCT_LEVEL_SKU, psdMatchesCatalogSku } from "./mockups/catalogSkuChoices";
import ImageAPI from "../../../api/ImageAPI";

const DEFAULT_MOCKUPS_CATALOG_PAGE_SIZE = 12;
type MockupStatusTab =
  | "all"
  | "pending"
  | "changes_requested"
  | "approved"
  | "not_uploaded"
  | "deleted";

const SwagstoreMockupsStep: React.FC = observer(() => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { showError, showSuccess } = useNotification();
  const { mockupConfigStore, swagStoreStore, userStore } = useStores();

  const [selectedGeneratorProduct, setSelectedGeneratorProduct] = useState<Swagpack | null>(null);
  const [selectedVariantSku, setSelectedVariantSku] = useState(PRODUCT_LEVEL_SKU);
  const [selectedManagerProduct, setSelectedManagerProduct] = useState<Swagpack | null>(null);
  const [returnToManagerProduct, setReturnToManagerProduct] = useState<Swagpack | null>(null);
  const [modalInitialTab, setModalInitialTab] = useState<"generate" | "exports" | "upload">("generate");
  const [editContext, setEditContext] = useState<{
    mockupId: number;
    psdTemplateId?: number;
  } | null>(null);
  const [openingEditGenerator, setOpeningEditGenerator] = useState(false);
  const [printingFilesProduct, setPrintingFilesProduct] = useState<Swagpack | null>(null);
  const selectedContextProduct = selectedGeneratorProduct ?? selectedManagerProduct;
  const querySearchInput = searchParams.get("mockups_search") ?? "";
  const queryAppliedSearch = searchParams.get("mockups_q") ?? "";
  const queryPage = Math.max(1, Number(searchParams.get("mockups_page") || "1") || 1);
  const queryLimit = Math.max(
    1,
    Number(
      searchParams.get("mockups_limit") || String(DEFAULT_MOCKUPS_CATALOG_PAGE_SIZE),
    ) || DEFAULT_MOCKUPS_CATALOG_PAGE_SIZE,
  );
  const queryCategoryIds = (searchParams.get("mockups_categories") ?? "")
    .split(",")
    .map((v) => Number(v.trim()))
    .filter((v) => Number.isFinite(v) && v > 0);
  const queryCategoryIdsKey = queryCategoryIds.join(",");
  const queryStatusTab = (searchParams.get("mockups_status") as MockupStatusTab) || "pending";
  const deriveNextProductStatusFromManager = (
    statuses: string[],
  ): "pending" | "changes_requested" | "approved" | "deleted" => {
    const normalized = statuses.map((s) => String(s || "").toLowerCase());
    const nonRejected = normalized.filter((s) => s !== "rejected");
    if (nonRejected.length === 0) return "pending";
    if (nonRejected.every((s) => s === "approved")) return "approved";
    if (nonRejected.some((s) => s === "changes_requested")) return "changes_requested";
    return "pending";
  };
  const querySection = searchParams.get("mockups_section");
  const queryManagerProductId = Number(
    searchParams.get("mockups_manager_product_id") || "0",
  );
  const queryManagerProductLabel =
    searchParams.get("mockups_manager_product_label") || "Product Mockups";

  useEffect(() => {
    mockupConfigStore
      .getPlacementTypes()
      .catch((e) =>
        showError(
          "Error",
          e?.response?.data?.message || "Failed to load placement types",
        ),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mockupConfigStore]);

  const storeId = swagStoreStore.storeId;
  const customerId = swagStoreStore.settings.storeDetails.customer_id;
  const canLoadProducts = Boolean(storeId && customerId);
  const catalogueKey = canLoadProducts ? `${storeId}:${customerId}` : null;

  useEffect(() => {
    if (!catalogueKey) return;
    swagStoreStore.resetMockupsCatalogueList();
  }, [catalogueKey, swagStoreStore]);

  useEffect(() => {
    if (querySection !== "manager") return;
    if (!Number.isFinite(queryManagerProductId) || queryManagerProductId <= 0) return;
    const fromCatalogue = swagStoreStore.mockupsCatalogueProducts.find(
      (product) => Number(product.id) === queryManagerProductId,
    );
    if (fromCatalogue && catalogSkuChoices(fromCatalogue).length > 1) {
      setSelectedManagerProduct(fromCatalogue);
      setSelectedGeneratorProduct(null);
      return;
    }
    if (selectedManagerProduct?.id) return;
    setSelectedGeneratorProduct(null);
    setSelectedManagerProduct({
      id: queryManagerProductId,
      label: queryManagerProductLabel,
      quantity: 0,
      available_quantity: 0,
      products_count: 0,
      used_quantity: 0,
      redeemQuantity: 0,
    });
  }, [
    querySection,
    queryManagerProductId,
    queryManagerProductLabel,
    selectedManagerProduct?.id,
    selectedManagerProduct?.products?.length,
    swagStoreStore.mockupsCatalogueProducts,
  ]);

  useEffect(() => {
    if (!canLoadProducts) return;
    if ((swagStoreStore.categories?.length ?? 0) > 0) return;
    void swagStoreStore.getCategories().catch((e) => {
      showError(
        "Error",
        e?.response?.data?.message || "Failed to load categories",
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canLoadProducts, swagStoreStore]);

  useEffect(() => {
    if (!catalogueKey) return;
    void swagStoreStore
      .fetchMockupsCataloguePage({
        page: queryPage,
        pageSize: queryLimit,
        search: queryAppliedSearch || undefined,
        categoryIds: queryCategoryIds,
        mockupStatus: queryStatusTab,
      })
      .catch((e) => {
        showError(
          "Error",
          e?.response?.data?.message || "Failed to load products",
        );
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    catalogueKey,
    queryPage,
    queryLimit,
    queryAppliedSearch,
    queryStatusTab,
    queryCategoryIdsKey,
    swagStoreStore,
  ]);

  const handleSelectProduct = async (
    product: Swagpack,
    initialTab: "generate" | "exports" | "upload" = "generate",
  ) => {
    mockupConfigStore.resetGeneratedMockupData();
    const catalogueChanged = mockupConfigStore.catalogueId !== product.id;
    if (catalogueChanged) {
      mockupConfigStore.resetProductData();
      mockupConfigStore.setCatalogueId(product.id);
    }
    const shouldLoadPsd =
      catalogueChanged || mockupConfigStore.psdTemplates.length === 0;
    const loadPromise = shouldLoadPsd
      ? mockupConfigStore.getPSDTemplates(product.id)
      : Promise.resolve();

    return loadPromise
      .then(() => {
        setModalInitialTab(initialTab);
      })
      .catch((e) => {
        showError(
          "Error",
          e?.response?.data?.message || "Failed to load PSD templates",
        );
      });
  };

  useEffect(() => {
    const selectedPsdId = mockupConfigStore.selectedPsdId;
    if (!selectedContextProduct?.id || !storeId || !selectedPsdId) return;
    void mockupConfigStore.fetchGeneratedMockups({
      store_id: String(storeId),
      product_id: selectedContextProduct.id,
      psd_template_id: selectedPsdId,
    }).catch((e) => {
      showError(
        "Failed to load previous mockups",
        e?.response?.data?.message || "Failed to load previous mockups",
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mockupConfigStore, selectedContextProduct, storeId, mockupConfigStore.selectedPsdId]);

  const catalogueProducts = swagStoreStore.mockupsCatalogueProducts;
  const loadingMockupsCatalogue = swagStoreStore.loadingMockupsCatalogue;
  const mockupsListHydrated = Boolean(
    catalogueKey && swagStoreStore.mockupsCatalogueHydratedKey === catalogueKey,
  );

  const categoryOptions = useMemo<FilterValue[]>(() => {
    const all: FilterValue = { id: "__all__", value: "All categories" };
    const rest =
      swagStoreStore.categories?.map((c) => ({
        id: String(c.category_id),
        value: c.category_name || `Category ${c.category_id}`,
      })) ?? [];
    return [all, ...rest];
  }, [swagStoreStore.categories]);

  const mockupsTotalCount = swagStoreStore.mockupsCatalogueTotalCount;
  const statusCounts = swagStoreStore.mockupsCatalogueStatusCounts;
  const gridStatusTab = queryStatusTab;
  const mockupsPaginationCount = Math.max(
    1,
    swagStoreStore.mockupsCatalogueTotalPages ||
    Math.ceil(mockupsTotalCount / queryLimit) ||
    0,
  );
  const showMockupsCatalogueSection =
    canLoadProducts &&
    (loadingMockupsCatalogue || mockupsListHydrated || catalogueProducts.length > 0);
  const selectedCategoryValues = queryCategoryIds.map(String);

  const setMockupsQueryParams = useCallback(
    (next: {
      searchInput?: string;
      appliedSearch?: string;
      page?: number;
      limit?: number;
      categoryIds?: number[];
      statusTab?: MockupStatusTab;
    }) => {
      const updated = new URLSearchParams(searchParams);
      const searchInput = next.searchInput ?? querySearchInput;
      const appliedSearch = next.appliedSearch ?? queryAppliedSearch;
      const page = next.page ?? queryPage;
      const limit = next.limit ?? queryLimit;
      const categoryIds = next.categoryIds ?? queryCategoryIds;
      const statusTab = next.statusTab ?? queryStatusTab;

      if (searchInput) updated.set("mockups_search", searchInput);
      else updated.delete("mockups_search");

      if (appliedSearch) updated.set("mockups_q", appliedSearch);
      else updated.delete("mockups_q");

      if (page > 1) updated.set("mockups_page", String(page));
      else updated.delete("mockups_page");

      if (limit !== DEFAULT_MOCKUPS_CATALOG_PAGE_SIZE) {
        updated.set("mockups_limit", String(limit));
      } else {
        updated.delete("mockups_limit");
      }

      if (categoryIds.length > 0) {
        updated.set("mockups_categories", categoryIds.join(","));
      } else {
        updated.delete("mockups_categories");
      }
      updated.set("mockups_status", statusTab);
      if (updated.toString() !== searchParams.toString()) {
        setSearchParams(updated, { replace: true });
      }
    },
    [
      queryAppliedSearch,
      queryCategoryIds,
      queryPage,
      queryLimit,
      querySearchInput,
      queryStatusTab,
      searchParams,
      setSearchParams,
    ],
  );

  const setMockupsSectionParams = useCallback(
    (next: { section: "manager" | null; product?: Swagpack | null }) => {
      const updated = new URLSearchParams(searchParams);
      if (next.section === "manager" && next.product?.id) {
        updated.set("mockups_section", "manager");
        updated.set("mockups_manager_product_id", String(next.product.id));
        updated.set(
          "mockups_manager_product_label",
          next.product.label || "Product Mockups",
        );
      } else {
        updated.delete("mockups_section");
        updated.delete("mockups_manager_product_id");
        updated.delete("mockups_manager_product_label");
      }
      if (updated.toString() !== searchParams.toString()) {
        setSearchParams(updated, { replace: true });
      }
    },
    [searchParams, setSearchParams],
  );

  const currentGeneratedKey =
    storeId && selectedContextProduct?.id && mockupConfigStore.selectedPsdId
      ? `${storeId}:${selectedContextProduct?.id}:${mockupConfigStore.selectedPsdId}`
      : null;
  const savedMockups = (
    currentGeneratedKey &&
      mockupConfigStore.lastGeneratedMockupsKey === currentGeneratedKey
      ? mockupConfigStore.generatedMockups
      : []
  ).filter((mockup) => mockupMatchesCatalogSku(mockup.variant_sku, selectedVariantSku));

  const psdTemplatesForSku = useMemo(
    () =>
      mockupConfigStore.psdTemplates.filter((template) =>
        psdMatchesCatalogSku(template.variant_sku, selectedVariantSku),
      ),
    [mockupConfigStore.psdTemplates, selectedVariantSku],
  );

  useEffect(() => {
    if (editContext) return;
    const stillValid = psdTemplatesForSku.some(
      (template) => template.id === mockupConfigStore.selectedPsdId,
    );
    if (stillValid) return;
    const nextId = psdTemplatesForSku[0]?.id ?? null;
    if (mockupConfigStore.selectedPsdId !== nextId) {
      mockupConfigStore.setSelectedPsdId(nextId);
    }
  }, [
    psdTemplatesForSku,
    mockupConfigStore,
    mockupConfigStore.selectedPsdId,
    editContext,
  ]);

  useEffect(() => {
    if (!selectedManagerProduct?.id || !storeId) {
      mockupConfigStore.clearManagerGeneratedMockups();
      return;
    }
    void mockupConfigStore.fetchManagerGeneratedMockupsByProduct({
      store_id: String(storeId),
      product_id: selectedManagerProduct.id!,
      include_inactive: queryStatusTab === "deleted",
    }, { force: false }).catch((e) => {
      showError(
        "Failed to load mockups",
        e?.response?.data?.message || "Failed to load mockups",
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedManagerProduct?.id, storeId, queryStatusTab, mockupConfigStore]);

  useEffect(() => {
    if (!selectedGeneratorProduct?.id) return;
    void swagStoreStore.loadUploadedMockupLogos().catch((e) => {
      showError(
        "Failed to load logos",
        e?.response?.data?.message || "Failed to load logos",
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGeneratorProduct?.id, storeId, customerId]);

  useEffect(() => {
    if (editContext) return;
    setSelectedVariantSku(PRODUCT_LEVEL_SKU);
  }, [selectedGeneratorProduct?.id, editContext]);

  const handleGenerateMockup = (payload: {
    psd_template_id: number;
    logoUrl?: string | null;
    x: number;
    y: number;
    width: number;
    height: number;
    rotation: number;
    template_required?: boolean;
    printing_type?: string;
    logo_size?: string;
    rgb_code?: string;
    cmyk_code?: string;
  }) => {
    const resolvedStoreId = swagStoreStore.storeId ? String(swagStoreStore.storeId) : null;
    const resolvedCustomerId = swagStoreStore.settings.storeDetails.customer_id
      ? Number(swagStoreStore.settings.storeDetails.customer_id)
      : null;

    if (!resolvedCustomerId) {
      showError("Error", "Customer id not found.");
      return;
    }

    mockupConfigStore.setGeneratingMockup(true);
    let logoUrl = payload.logoUrl ?? null;

    if (!logoUrl) {
      showError("Error", "Please upload to library and select a logo.");
      mockupConfigStore.setGeneratingMockup(false);
      return;
    }
    const variantSku = catalogSkuForRequest(selectedVariantSku);
    if (!variantSku) {
      showError(
        "SKU required",
        "Select a SKU. New mockups are saved against one SKU.",
      );
      mockupConfigStore.setGeneratingMockup(false);
      return;
    }
    const psdMatchesSku = psdTemplatesForSku.some(
      (template) => template.id === payload.psd_template_id,
    );
    if (!psdMatchesSku) {
      showError(
        "No PSD",
        "This SKU has no PSD. A product PSD is not used for another color.",
      );
      mockupConfigStore.setGeneratingMockup(false);
      return;
    }
    if (!selectedGeneratorProduct?.id || !resolvedStoreId) {
      showError("Error", "Product or store not found.");
      mockupConfigStore.setGeneratingMockup(false);
      return;
    }
    return mockupConfigStore.mockupConfigAPI.generateMockup({
      psd_template_id: payload.psd_template_id,
      generated_mockup_id: editContext?.mockupId,
      customer_id: resolvedCustomerId,
      store_id: resolvedStoreId,
      logo_url: logoUrl,
      x: payload.x,
      y: payload.y,
      width: payload.width,
      height: payload.height,
      rotation: payload.rotation,
      template_required: payload.template_required ?? false,
      printing_type: payload.printing_type,
      product_name: selectedGeneratorProduct?.label || undefined,
      logo_size: payload.logo_size,
      rgb_code: payload.rgb_code,
      cmyk_code: payload.cmyk_code,
      variant_sku: variantSku,
    })
      .then((result) => {
        mockupConfigStore.setGeneratedMockupOutputs(result.outputs ?? null);
        mockupConfigStore.setMockupReferenceId(result.reference_id ?? null);
        if (selectedGeneratorProduct?.id) {
          swagStoreStore.applyLocalMockupStatusTransition({
            productId: selectedGeneratorProduct.id,
            nextStatus: "pending",
            currentTab: queryStatusTab,
            incrementMockupCounts: !editContext?.mockupId,
          });
        }
        if (resolvedStoreId && selectedGeneratorProduct?.id) {
          return mockupConfigStore.fetchGeneratedMockups(
            {
              store_id: resolvedStoreId,
              product_id: selectedGeneratorProduct.id,
              psd_template_id: payload.psd_template_id,
            },
            { force: true },
          );
        }
      })
      .catch((e: any) => {
        showError(
          "Generation failed",
          e?.response?.data?.message ||
          "Failed to generate mockup",
        );
      })
      .finally(() => {
        mockupConfigStore.setGeneratingMockup(false);
      });
  };

  const handleUploadLogoToLibrary = (file: File) => {
    if (!selectedGeneratorProduct?.id || !customerId) return;
    swagStoreStore.uploadMockupLogo(file).catch((e) => {
      showError(
        "Failed to save logo",
        e?.response?.data?.message || e?.message || "Failed to save logo",
      );
    });
  };

  const handleDeleteUploadedLogo = async (logoId: number) => {
    if (!selectedGeneratorProduct?.id || !customerId) return;
    swagStoreStore.deleteMockupLogo(logoId).catch((error) => {
      showError(
        "Error deleting uploaded logo",
        error?.response?.data?.message || "Failed to delete uploaded logo",
      );
    });
  };

  const handleDeleteMockup = async (id: number) => {
    mockupConfigStore.deleteGeneratedMockup(id).catch((error) => {
      showError(
        "Error deleting mockup",
        error?.response?.data?.message || "Failed to delete mockup",
      );
    });

  };

  const handleUploadReplacementMockup = async (payload: {
    id: number;
    file: File;
  }) => {
    try {
      const urls = await new ImageAPI().uploadImage([payload.file], "mockup-renders");
      const url = urls?.[0];
      if (!url) {
        showError("Error", "Failed to upload replacement mockup");
        return;
      }
      mockupConfigStore.updateGeneratedMockup({
        id: payload.id,
        mockup_url: url,
        source: "manual",
      }).then(() => {
        if (selectedManagerProduct?.id) {
          swagStoreStore.applyLocalMockupStatusTransition({
            productId: selectedManagerProduct.id,
            nextStatus: "pending",
            currentTab: queryStatusTab,
          });
        }
        const now = new Date().toISOString();
        mockupConfigStore.managerGeneratedMockups =
          mockupConfigStore.managerGeneratedMockups.map((m) =>
            m.id === payload.id
              ? {
                ...m,
                mockup_url: url,
                source: "manual",
                updated_at: now,
              }
              : m,
          );
        mockupConfigStore.generatedMockups = mockupConfigStore.generatedMockups.map(
          (m) =>
            m.id === payload.id
              ? {
                ...m,
                mockup_url: url,
                source: "manual",
                updated_at: now,
              }
              : m,
        );
      }).catch((e) => {
        showError(
          "Failed to upload replacement mockup",
          e?.response?.data?.message || "Failed to upload replacement mockup",
        );
      });

    } catch (error: any) {
      showError(
        "Upload failed",
        error?.response?.data?.message ||
        error?.message ||
        "Failed to replace mockup",
      );
    }
  };

  const handleUploadMockupFiles = async (files: File[]) => {
    const productId = selectedGeneratorProduct?.id;
    const resolvedStoreId = storeId ? String(storeId) : null;
    const resolvedCustomerId = customerId ? Number(customerId) : null;

    if (!productId || !resolvedStoreId || !resolvedCustomerId) {
      showError("Error", "Product, store, or customer not found");
      return;
    }
    if (!files?.length) {
      showError("Error", "Please select an image to upload");
      return;
    }
    const variantSku = catalogSkuForRequest(selectedVariantSku);
    if (!variantSku) {
      showError(
        "SKU required",
        "Select a SKU. New mockups are saved against one SKU.",
      );
      return;
    }

    const psdTemplateId =
      psdTemplatesForSku.find(
        (template) => template.id === mockupConfigStore.selectedPsdId,
      )?.id ?? null;

    try {
      const uploadedUrls = await new ImageAPI().uploadImage(files, "mockup-renders");
      if (!uploadedUrls.length) {
        showError("Upload failed", "Failed to upload mockup image");
        return;
      }

      await mockupConfigStore.uploadManualMockup({
        customer_id: resolvedCustomerId,
        store_id: resolvedStoreId,
        product_id: productId,
        mockup_urls: uploadedUrls,
        psd_template_id: psdTemplateId,
        variant_sku: variantSku,
      });

      swagStoreStore.applyLocalMockupStatusTransition({
        productId,
        nextStatus: "pending",
        currentTab: queryStatusTab,
        incrementMockupCounts: true,
      });

      if (psdTemplateId) {
        if (mockupConfigStore.selectedPsdId !== psdTemplateId) {
          mockupConfigStore.setSelectedPsdId(psdTemplateId);
        }
        await mockupConfigStore.fetchGeneratedMockups(
          {
            store_id: resolvedStoreId,
            product_id: productId,
            psd_template_id: psdTemplateId,
          },
          { force: true },
        );
      }

      setModalInitialTab("exports");
      showSuccess("Mockup image uploaded successfully");
    } catch (error: any) {
      showError(
        "Upload failed",
        error?.response?.data?.message ||
        error?.message ||
        "Failed to upload mockup",
      );
    }
  };

  const handleApproveOnBehalfUser = async (payload: {
    id: number;
    reason: string;
    file: File;
  }) => {
    void new ImageAPI().uploadImage([payload.file], "mockup-approval-evidence").then((urls) => {
      const url = urls?.[0];
      if (!url) {
        showError("Error", "Failed to upload evidence image");
        return;
      }
      mockupConfigStore.approveGeneratedMockupOnBehalfUser({
        id: payload.id,
        reason: payload.reason,
        evidence_url: url,
      }).then(() => {
        if (selectedManagerProduct?.id) {
          const statuses = mockupConfigStore.managerGeneratedMockups.map((m) =>
            String(m.approval_status ?? GeneratedMockupApprovalStatus.PENDING).toLowerCase(),
          );
          const nextStatus = deriveNextProductStatusFromManager(statuses);
          swagStoreStore.applyLocalMockupStatusTransition({
            productId: selectedManagerProduct.id,
            nextStatus,
            currentTab: queryStatusTab,
          });
        }
      }).catch((e) => {
        showError(
          "Approve on behalf failed",
          e?.response?.data?.message || "Failed to approve on behalf",
        );
      });
    }).catch((e) => {
      showError(
        "Upload failed",
        e?.response?.data?.message || e?.message || "Failed to upload evidence image",
      );
    });
  };

  const handleShowOnStorefront = async (id: number, showOnStorefront: boolean) => {
    void mockupConfigStore
      .setGeneratedMockupShowOnStorefront(id, showOnStorefront)
      .catch((e) => {
        showError(
          "Update failed",
          e?.response?.data?.message || "Failed to update storefront visibility",
        );
      });
  };

  const handleApproveMockup = async (id: number) => {
    void mockupConfigStore.approveGeneratedMockup(id).then(() => {
      if (selectedManagerProduct?.id) {
        const statuses = mockupConfigStore.managerGeneratedMockups.map((m) =>
          String(m.approval_status ?? GeneratedMockupApprovalStatus.PENDING).toLowerCase(),
        );
        const nextStatus = deriveNextProductStatusFromManager(statuses);
        swagStoreStore.applyLocalMockupStatusTransition({
          productId: selectedManagerProduct.id,
          nextStatus,
          currentTab: queryStatusTab,
        });
      }
    }).catch((e) => {
      showError(
        "Approval failed",
        e?.response?.data?.message || "Failed to approve mockup",
      );
    });
  };

  const handleRejectMockup = async (payload: {
    id: number;
    rejection_reason: string;
    file?: File;
    evidence_url?: string;
  }) => {
    if (payload.file) {
      const urls = await new ImageAPI().uploadImage([payload.file], "mockup-approval-evidence");
      const evidenceUrl = urls?.[0];
      if (!evidenceUrl) {
        showError("Error", "Failed to upload evidence image");
        return;
      }
      payload = { ...payload, evidence_url: evidenceUrl };
    }
    void mockupConfigStore.rejectGeneratedMockup(payload).then(() => {
      if (selectedManagerProduct?.id) {
        const statuses = mockupConfigStore.managerGeneratedMockups.map((m) =>
          String(m.approval_status ?? GeneratedMockupApprovalStatus.PENDING).toLowerCase(),
        );
        // If no active mockups remain, product should move out of current active tab to deleted.
        if (statuses.length === 0) {
          swagStoreStore.markProductAsLocallyDeleted(selectedManagerProduct.id);
          swagStoreStore.applyLocalMockupStatusTransition({
            productId: selectedManagerProduct.id,
            previousStatus:
              queryStatusTab === "pending" ||
                queryStatusTab === "approved" ||
                queryStatusTab === "changes_requested"
                ? queryStatusTab
                : undefined,
            nextStatus: "deleted",
            currentTab: queryStatusTab,
          });
        } else {
          const nextStatus = deriveNextProductStatusFromManager(statuses);
          swagStoreStore.applyLocalMockupStatusTransition({
            productId: selectedManagerProduct.id,
            nextStatus,
            currentTab: queryStatusTab,
          });
          // Product still has active mockups, but now also belongs to deleted tab.
          swagStoreStore.markProductAsLocallyDeleted(selectedManagerProduct.id);
        }
      }
    }).catch((e) => {
      showError(
        "Rejection failed",
        e?.response?.data?.message || "Failed to reject mockup",
      );
    });
  };

  const handleFetchGeneratedMockupComments = useCallback(
    async (id: number) => {
      await mockupConfigStore.fetchGeneratedMockupComments(id);
    },
    [mockupConfigStore],
  );

  const handleAddMockupComment = async (payload: {
    generated_mockup_id: number;
    comment: string;
  }) => {
    await mockupConfigStore.addGeneratedMockupComment(payload);
    if (selectedManagerProduct?.id && queryStatusTab === "pending") {
      swagStoreStore.applyLocalMockupStatusTransition({
        productId: selectedManagerProduct.id,
        previousStatus: "pending",
        nextStatus: "changes_requested",
        currentTab: queryStatusTab,
      });
    }
  };

  const openGeneratorForProduct = async (
    product: Swagpack,
    initialTab: "generate" | "exports" | "upload" = "generate",
  ) => {
    setMockupsSectionParams({ section: null });
    setReturnToManagerProduct(null);
    setEditContext(null);
    await handleSelectProduct(product, initialTab);
    setSelectedManagerProduct(null);
    setSelectedGeneratorProduct(product);
  };

  const openManagerForProduct = async (product: Swagpack) => {
    setReturnToManagerProduct(null);
    setEditContext(null);
    await handleSelectProduct(product, "exports");
    setSelectedGeneratorProduct(null);
    setSelectedManagerProduct(product);
    setMockupsSectionParams({ section: "manager", product });
  };

  const mockupGeneratorModal = (
    <MockupGeneratorModal
      open={
        Boolean(selectedGeneratorProduct) &&
        !mockupConfigStore.loadingPsd &&
        !openingEditGenerator
      }
      product={selectedGeneratorProduct}
      variantSku={selectedVariantSku}
      onVariantSkuChange={setSelectedVariantSku}
      onClose={() => {
        if (returnToManagerProduct) {
          if (storeId && returnToManagerProduct.id) {
            void mockupConfigStore.fetchManagerGeneratedMockupsByProduct(
              {
                store_id: String(storeId),
                product_id: returnToManagerProduct.id,
                include_inactive: queryStatusTab === "deleted",
              },
              { force: true },
            ).catch((e) => {
              showError(
                "Failed to load mockups",
                e?.response?.data?.message || "Failed to load mockups",
              );
            });
          }
          setSelectedManagerProduct(returnToManagerProduct);
          setMockupsSectionParams({
            section: "manager",
            product: returnToManagerProduct,
          });
        }
        setSelectedGeneratorProduct(null);
        setReturnToManagerProduct(null);
        setEditContext(null);
      }}
      initialTab={modalInitialTab}
      placementTypes={mockupConfigStore.placementTypes}
      psdTemplates={psdTemplatesForSku}
      selectedPsdId={mockupConfigStore.selectedPsdId}
      loadingPsd={mockupConfigStore.loadingPsd}
      savedMockups={savedMockups}
      mockupOutputs={mockupConfigStore.mockupOutputs}
      mockupReferenceId={mockupConfigStore.mockupReferenceId}
      generatingMockup={mockupConfigStore.generatingMockup}
      updatingMockupId={mockupConfigStore.updatingGeneratedMockupId}
      deletingGeneratedMockupId={mockupConfigStore.deletingGeneratedMockupId}
      lockPsdSelection={Boolean(editContext)}
      editMode={Boolean(editContext)}
      editMockupId={editContext?.mockupId ?? null}
      onSelectPsdId={(id: number | null) => {
        if (editContext) return;
        mockupConfigStore.resetGeneratedMockupData();
        mockupConfigStore.setSelectedPsdId(id);
      }}
      onGenerateMockup={handleGenerateMockup}
      uploadedLogos={swagStoreStore.uploadedMockupLogos}
      loadingUploadedLogos={swagStoreStore.loadingUploadedMockupLogos}
      onUploadLogoToLibrary={handleUploadLogoToLibrary}
      onDeleteUploadedLogo={handleDeleteUploadedLogo}
      onDeleteMockup={handleDeleteMockup}
      onManualUploadMockup={handleUploadReplacementMockup}
      onUploadMockupFiles={handleUploadMockupFiles}
      onError={(title, message) => showError(title, message ?? "")}
    />
  );

  return (
    <>
      <StorePrintingFilesDialog
        open={Boolean(printingFilesProduct)}
        product={printingFilesProduct}
        storeId={storeId}
        onClose={() => setPrintingFilesProduct(null)}
      />
      {selectedManagerProduct && !mockupConfigStore.loadingPsd ? (
        <>
          <AppLoader loading={openingEditGenerator || mockupConfigStore.loadingManagerGeneratedMockups} />
          {!mockupConfigStore.loadingManagerGeneratedMockups && (
            <SwagstoreMockupManagerPage
              product={selectedManagerProduct}
              onBack={() => {
                setSelectedManagerProduct(null);
                setMockupsSectionParams({ section: null });
              }}
              mockups={mockupConfigStore.managerGeneratedMockups}
              comments={mockupConfigStore.generatedMockupComments}
              loadingComments={mockupConfigStore.loadingGeneratedMockupComments}
              postingComment={mockupConfigStore.postingGeneratedMockupComment}
              approvingMockupId={mockupConfigStore.approvingGeneratedMockupId}
              rejectingMockupId={mockupConfigStore.rejectingGeneratedMockupId}
              approvingOnBehalfMockupId={mockupConfigStore.approvingOnBehalfGeneratedMockupId}
              deletingMockupId={mockupConfigStore.deletingGeneratedMockupId}
              canRejectApprovedMockup={userStore.hasPermission(
                EmployeeRoles.REJECT_APPROVED_MOCKUPS,
                true
              )}
              onFetchComments={handleFetchGeneratedMockupComments}
              onAddComment={handleAddMockupComment}
              onApproveMockup={handleApproveMockup}
              onRejectMockup={handleRejectMockup}
              onApproveOnBehalfUser={handleApproveOnBehalfUser}
              onShowOnStorefront={handleShowOnStorefront}
              updatingShowOnStorefrontId={mockupConfigStore.updatingShowOnStorefrontId}
              onDeleteMockup={handleDeleteMockup}
              onEditInGenerator={async (payload) => {
                const product = selectedManagerProduct;
                if (!product?.id) return;
                setOpeningEditGenerator(true);
                try {
                  await handleSelectProduct(product, "generate");
                  setReturnToManagerProduct(product);
                  setEditContext({
                    mockupId: payload.mockupId,
                    psdTemplateId: payload.psdTemplateId,
                  });
                  const sku = String(payload.variantSku ?? "").trim();
                  if (sku) setSelectedVariantSku(sku);
                  if (payload.psdTemplateId != null) {
                    mockupConfigStore.setSelectedPsdId(payload.psdTemplateId);
                  }
                  setModalInitialTab("generate");
                  setSelectedGeneratorProduct(product);
                } catch (e) {
                  showError(
                    "Error",
                    (e as Error)?.message || "Failed to open generator",
                  );
                } finally {
                  setOpeningEditGenerator(false);
                }
              }}
              onError={(title, message) => showError(title, message)}
            />
          )}
        </>
      ) : (
        <Box
          sx={{
            p: { xs: 2, md: 3 },
            pb: 4,
            mb: 4,
            maxWidth: 1240,
            mx: "auto",
          }}
        >
          <PageTitle
            title="Mockups"
            fontSize="1.25rem"
            paddingTop={0}
            paddingBottom={0.5}
          />
          <FormContent
            content=""
            marginBottom={2}
            fontSize="14px"
          />

          {!swagStoreStore.storeId && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Save store details first to load products for mockups.
            </Alert>
          )}

          <AppLoader
            loading={
              (Boolean(selectedContextProduct) && mockupConfigStore.loadingPsd)
            }
          />

          {showMockupsCatalogueSection ? (
            <Paper
              variant="outlined"
              sx={{
                p: { xs: 2, md: 2.5 },
                mb: 2,
                borderRadius: 2.5,
                borderColor: "rgba(15, 23, 42, 0.08)",
                boxShadow: "0 1px 2px rgba(15, 23, 42, 0.03)",
              }}
            >
              {/* <FormTitle title="Products" marginBottom={1} /> */}
              <Tabs
                value={queryStatusTab}
                onChange={(_, value: MockupStatusTab) =>
                  setMockupsQueryParams({
                    statusTab: value,
                    page: 1,
                  })
                }
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  mb: 2,
                  minHeight: 36,
                  "& .MuiTabs-flexContainer": {
                    flexWrap: "nowrap",
                  },
                  "& .MuiTabs-indicator": {
                    height: 2.5,
                    borderRadius: 2,
                  },
                }}
              >
                <Tab
                  value="all"
                  label={`All (${statusCounts.all})`}
                  sx={{ minHeight: 36, px: 2, textTransform: "none", fontWeight: 600 }}
                />
                <Tab
                  value="pending"
                  label={`User Approval Pending (${statusCounts.pending})`}
                  sx={{ minHeight: 36, px: 2, textTransform: "none", fontWeight: 600 }}
                />
                <Tab
                  value="changes_requested"
                  label={`Needs Action (${statusCounts.changes_requested})`}
                  sx={{ minHeight: 36, px: 2, textTransform: "none", fontWeight: 600 }}
                />
                <Tab
                  value="not_uploaded"
                  label={`Not Uploaded (${statusCounts.not_uploaded})`}
                  sx={{ minHeight: 36, px: 2, textTransform: "none", fontWeight: 600 }}
                />
                <Tab
                  value="approved"
                  label={`Approved (${statusCounts.approved})`}
                  sx={{ minHeight: 36, px: 2, textTransform: "none", fontWeight: 600 }}
                />
                <Tab
                  value="deleted"
                  label={`Deleted (${statusCounts.deleted})`}
                  sx={{ minHeight: 36, px: 2, textTransform: "none", fontWeight: 600 }}
                />
              </Tabs>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                sx={{
                  mb: 2,
                  alignItems: { md: "flex-start" },
                  flexWrap: { md: "wrap" },
                  "@media (min-width:1250px)": {
                    flexWrap: "nowrap",
                  },
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    flex: { md: "2 1 360px" },
                    minWidth: { md: 260 },
                    "@media (min-width:1250px)": {
                      flex: "2 1 0",
                      minWidth: 0,
                    },
                  }}
                >
                  <FormTextField
                    title="Search products"
                    placeholder="Search products..."
                    value={querySearchInput}
                    onChange={(_, value) => {
                      const nextValue = value || "";
                      if (nextValue.trim() === "") {
                        setMockupsQueryParams({
                          searchInput: "",
                          appliedSearch: "",
                          page: 1,
                        });
                        return;
                      }
                      setMockupsQueryParams({
                        searchInput: nextValue,
                      });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        setMockupsQueryParams({
                          appliedSearch: querySearchInput.trim(),
                          page: 1,
                        });
                      }
                    }}
                    sx={{ mb: 0 }}
                  />
                </Box>
                <Box
                  sx={{
                    width: { xs: "100%", md: "auto" },
                    flex: { md: "1 1 220px" },
                    minWidth: { md: 200 },
                    "@media (min-width:1250px)": {
                      flex: "1.2 1 0",
                      minWidth: 180,
                    },
                  }}
                >
                  <AppDropDown
                    title="category"
                    isMultiSelect={true}
                    list={categoryOptions}
                    valuesList={selectedCategoryValues}
                    onMultiSelectionChange={(ids) => {
                      const sanitized = ids
                        .filter((id) => id !== "__all__")
                        .map((id) => Number(id))
                        .filter((id) => Number.isFinite(id) && id > 0);
                      setMockupsQueryParams({
                        categoryIds: sanitized,
                        page: 1,
                      });
                    }}
                    placeholderText=""
                    width="100%"
                  />
                </Box>
                <Box
                  sx={{
                    width: { xs: "100%", md: "auto" },
                    flex: { md: "0 0 auto" },
                    marginLeft: { md: "auto" },
                    "@media (min-width:1250px)": {
                      flex: "0 0 130px",
                      marginLeft: 0,
                    },
                  }}
                >
                  <AppButton
                    label="Clear Filters"
                    variant="secondary"
                    onClick={() =>
                      setMockupsQueryParams({
                        searchInput: "",
                        appliedSearch: "",
                        categoryIds: [],
                        page: 1,
                      })
                    }
                    customStyles={{
                      minHeight: "40px",
                      width: "100%",
                      minWidth: "120px",
                      whiteSpace: "nowrap",
                    }}
                  />
                </Box>
              </Stack>

              {loadingMockupsCatalogue && catalogueProducts.length === 0 ? (
                <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
                  <CircularProgress size={32} />
                </Box>
              ) : null}

              {catalogueProducts.length > 0 ? (
                <CatalogueProductGrid
                  products={catalogueProducts}
                  statusTab={gridStatusTab}
                  disableView={queryStatusTab === "not_uploaded"}
                  onGenerateProduct={(product) => {
                    void openGeneratorForProduct(product, "generate");
                  }}
                  onViewProduct={(product) => {
                    void openManagerForProduct(product);
                  }}
                  onUploadPrintingFiles={(product) => {
                    setPrintingFilesProduct(product);
                  }}
                />
              ) : null}

              {mockupsListHydrated &&
                !loadingMockupsCatalogue &&
                catalogueProducts.length === 0 ? (
                <Alert severity="info">
                  {queryAppliedSearch || queryCategoryIds.length > 0
                    ? "No products match your search or category."
                    : "No result found."}
                </Alert>
              ) : null}

              {queryPage > 1 ||
                swagStoreStore.mockupsCatalogueHasNextPage ||
                catalogueProducts.length > 0 ? (
                <AppTablePagenation
                  totalCount={
                    mockupsTotalCount > 0
                      ? mockupsTotalCount
                      : mockupsPaginationCount * queryLimit
                  }
                  limit={queryLimit}
                  pageNo={queryPage}
                  showRowsPerPageOptions={true}
                  showLabelRowsPerPage={true}
                  onPageChange={(_, page) => {
                    setMockupsQueryParams({ page });
                  }}
                  handleRowsChange={(limit) => {
                    const parsed = Number(limit);
                    const safeLimit = Number.isFinite(parsed) && parsed > 0
                      ? parsed
                      : DEFAULT_MOCKUPS_CATALOG_PAGE_SIZE;
                    setMockupsQueryParams({
                      page: 1,
                      limit: safeLimit,
                    });
                  }}
                />
              ) : null}
            </Paper>
          ) : null}
        </Box>
      )}
      {mockupGeneratorModal}
    </>
  );
});

export default SwagstoreMockupsStep;
